import React, { useMemo, useState, useEffect } from "react";
import cn from "classnames";

import FilterDatePicker from "components/datePicker/FilterDatePicker";

import "./filterDateRange.scss";
import { type GenericFiltersMap, type RangeFilterValues } from "utils/filterUtils";
import { type ErrorPosition, ErrorPositions } from "enums/errorPositions";

interface FilterDateRangeProps<T extends string> {
  propertyName: T;
  label?: string;
  filter: GenericFiltersMap<T>;
  updateFilter: (filter: GenericFiltersMap<T>) => void;
  verticalPosition?: "top" | "bottom";
  horizontalPositionFirstPicker?: "auto" | "left" | "right";
  horizontalPositionSecondPicker?: "auto" | "left" | "right";
  className?: string;
  disabled?: boolean;
  onValidationChange?: (propertyName: T, isValid: boolean) => void;
  errorPosition?: ErrorPosition;
}

function FilterDateRange<T extends string>({
  label,
  propertyName,
  filter,
  updateFilter,
  className,
  disabled,
  verticalPosition,
  horizontalPositionFirstPicker,
  horizontalPositionSecondPicker,
  onValidationChange,
  errorPosition = ErrorPositions.top,
}: FilterDateRangeProps<T>) {
  const [isFromValid, setIsFromValid] = useState(true);
  const [isToValid, setIsToValid] = useState(true);

  useEffect(() => {
    const bothValid = isFromValid && isToValid;
    onValidationChange?.(propertyName, bothValid);
  }, [isFromValid, isToValid, propertyName, onValidationChange]);

  const handleFromValidationChange = (_propertyName: T, isValid: boolean) => {
    setIsFromValid(isValid);
  };

  const handleToValidationChange = (_propertyName: T, isValid: boolean) => {
    setIsToValid(isValid);
  };

  const rangeValue = useMemo((): RangeFilterValues => {
    const filterValue: unknown = filter[propertyName];

    if (filterValue && typeof filterValue === "object" && ("from" in filterValue || "to" in filterValue)) {
      return filterValue as RangeFilterValues;
    }

    return { from: "", to: "" };
  }, [filter, propertyName]);

  return (
    <div className={cn("filter-date-range", className)}>
      {label && <div className="label">{label}</div>}
      <div className="bottom-row">
        <FilterDatePicker
          placeholder="From"
          propertyName={propertyName}
          filter={filter}
          updateFilter={updateFilter}
          disabled={disabled}
          verticalPosition={verticalPosition}
          horizontalPosition={horizontalPositionFirstPicker}
          maxDate={rangeValue.to}
          onValidationChange={handleFromValidationChange}
          errorPosition={errorPosition}
        />
        <span className="delimiter">-</span>
        <FilterDatePicker
          placeholder="To"
          propertyName={propertyName}
          filter={filter}
          updateFilter={updateFilter}
          disabled={disabled}
          verticalPosition={verticalPosition}
          horizontalPosition={horizontalPositionSecondPicker}
          popupPosition="bottom right"
          minDate={rangeValue.from}
          onValidationChange={handleToValidationChange}
          errorPosition={errorPosition}
        />
      </div>
    </div>
  );
}

export default FilterDateRange;
